import React, { useEffect } from 'react';
import { View, TextInput, TouchableOpacity, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Hoverable } from 'react-native-web-hover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import MyAppText from './MyAppText';

interface IncrementDecrementInputProps {
    value: number;
    setValue: (newValue: number) => void;
    maxValue?: number;
    label?: string;
    required?: boolean;
    viewStyle?: StyleProp<ViewStyle>;
    labelStyle?: StyleProp<TextStyle>;
}

export default function IncrementDecrementInput({ value, setValue, maxValue, label, labelStyle, viewStyle, required }: IncrementDecrementInputProps) {
    const { styles, theme } = useStyles(stylesheet);

    const increment = () => {
        setValue(value + 1);
    };

    const decrement = () => {
        setValue(value > 0 ? value - 1 : 0);
    };

    const handleChange = (text: string) => {
        const newValue = parseInt(text, 10);
        setValue(isNaN(newValue) ? 0 : newValue);
    };

    useEffect(() => {
        if (maxValue !== undefined && value > maxValue) {
            setValue(maxValue);
        }
    }, [maxValue]);

    return (
        <View style={[viewStyle]}>
            <View style={{ flexDirection: 'row', columnGap: 5 }}>
                {label && <MyAppText style={labelStyle ? labelStyle : styles.label(16)}>{label}</MyAppText>}
                {required ?
                    <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                    : null}
            </View>
            <View style={styles.container}>
                <TouchableOpacity style={{ flex: 1 }} onPress={decrement}>
                    <Hoverable style={{ flex: 1 }}>
                        {({ hovered }) => (
                            <View style={styles.buttonContainer(hovered, value <= 0)}>
                                <FontAwesomeIcon style={styles.buttonIcon} fontSize={12} icon={faMinus} color='white' />
                            </View>
                        )}
                    </Hoverable>
                </TouchableOpacity>
                <TextInput
                    style={styles.input}
                    keyboardType='numeric'
                    value={String(value)}
                    onChangeText={handleChange}
                />
                <TouchableOpacity disabled={maxValue !== undefined && value + 1 > maxValue} style={{ flex: 1 }} onPress={increment}>
                    <Hoverable style={{ flex: 1 }}>
                        {({ hovered }) => (
                            <View style={styles.buttonContainer(hovered, maxValue !== undefined && value + 1 > maxValue)}>
                                <FontAwesomeIcon style={styles.buttonIcon} fontSize={12} icon={faPlus} color='white' />
                            </View>
                        )}
                    </Hoverable>
                </TouchableOpacity>
            </View>
        </View>
    );
}

const stylesheet = createStyleSheet(theme => ({
    label: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
    }),
    input: {
        backgroundColor: theme.colors.container.background,
        borderColor: theme.colors.container.border,
        borderWidth: 1,
        height: 40,
        padding: 10,
        borderRadius: 4,
        color: theme.colors.field.text,
        flex: 1
    },
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        flex: 1
    },
    buttonIcon: {
        color: theme.colors.color,
    },
    buttonContainer: (hovered: boolean, disabled: boolean) => ({
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 5,
        paddingHorizontal: 30,
        width: '100%',
        columnGap: 10,
        height: 40,
        backgroundColor: disabled ? theme.colors.primaryButton.disabledBackground : hovered ? theme.colors.primaryButton.hoverBackground : theme.colors.primaryButton.background,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        justifyContent: 'center'
    }),
}));
